import { loadMoreProducts } from './load-more-products-btn.js';
import {
  debounce,
  trapFocus,
  removeTrapFocus,
  onKeyUpEscape
} from './utils.js';
import { getPromoTags } from './promo-tags.js';

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce(event => {
      this.onSubmitHandler(event);
    }, 200);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapperDesktop = this.querySelector('#FacetsWrapperDesktop');
    const facetWrapperMobile = this.querySelector('#FacetsWrapperMobile');
    if (facetWrapperDesktop)
      facetWrapperDesktop.addEventListener('keyup', onKeyUpEscape);
    if (facetWrapperMobile)
      facetWrapperMobile.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = event => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach(element => {
      element.classList.toggle('disabled', disable);
    });
  }

  /* Promise.all shaved 45ms from func end time */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    const fetchPromises = sections.map(section => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? Promise.resolve(
            FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          )
        : FacetFiltersForm.fetchSection(url);
    });

    Promise.all(fetchPromises).then(responses => {
      responses.forEach(responseText => {
        const html = responseText;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderPaginationStats(html);
      });
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static fetchSection(url) {
    return fetch(url).then(response => response.text());
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    setTimeout(() => {
      FacetFiltersForm.renderPaginationStats(html);
    }, 100);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;
    // add promo tags to the fetched product cards
    getPromoTags();
  }

  static renderPaginationStats(html) {
    try {
      const domParser = new DOMParser();

      // Parse the HTML and update the pagination
      const parsedDocument = domParser.parseFromString(html, 'text/html');
      const paginationElement =
        parsedDocument.getElementById('search-pagination');

      if (paginationElement) {
        document.getElementById('search-pagination').innerHTML =
          paginationElement.innerHTML;
      }
    } catch (error) {
      console.error('Error parsing pagination HTML: ', error);
    }

    loadMoreProducts();
  }

  static renderProductCount(html) {
    let count = '';

    try {
      const domParser = new DOMParser();

      // Parse the HTML and retrieve the count
      const parsedDocument = domParser.parseFromString(html, 'text/html');
      const parsedProductCountElements =
        parsedDocument.querySelectorAll('#js-ProductCount');

      if (parsedProductCountElements.length > 0) {
        count = parsedProductCountElements[0]?.innerHTML || '';
      }

      // update the DOM
      const DOMparsedProductCountElements =
        document.querySelectorAll('#js-ProductCount');
      const activeFacetBtns = document.querySelectorAll('facet-remove');

      if (DOMparsedProductCountElements.length > 0) {
        DOMparsedProductCountElements.forEach(countElement => {
          countElement.innerHTML = count;
        });
      }

      // update mobile facets `VIEW ITEMS` button with the count
      const mobileViewItemsBtn = document.querySelector('.filters-apply');
      const mobileViewItemsCount = document.querySelector(
        '.js-filters__footer--results-count'
      );
      if (mobileViewItemsBtn && mobileViewItemsCount) {
        // remove the word `product(s)` from count
        let filterProductCount = count.trim().split(' ')[0];
        mobileViewItemsCount.innerText = `(${filterProductCount})`;
        mobileViewItemsCount.style.display = 'inline-block';
        // Checking for 2 because 'Clear All' is always in the DOM
        if (activeFacetBtns && activeFacetBtns.length <= 2) {
          mobileViewItemsCount.innerText = '';
        }
      }
    } catch (error) {
      console.error('Error parsing HTML: ', error);
    }
  }

  static renderFilters(html, event) {
    try {
      const domParser = new DOMParser();

      // Parse the HTML
      const parsedHTML = domParser.parseFromString(html, 'text/html');

      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
      );
      const facetDetailsElementsFromDom = document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter'
      );

      // Remove facets that are no longer returned from the server
      Array.from(facetDetailsElementsFromDom).forEach(currentElement => {
        if (
          !Array.from(facetDetailsElementsFromFetch).some(
            ({ id }) => currentElement.id === id
          )
        ) {
          currentElement.remove();
        }
      });

      const matchesId = element => {
        const jsFilter = event ? event.target.closest('.js-filter') : undefined;
        return jsFilter ? element.id === jsFilter.id : false;
      };

      const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
        element => !matchesId(element)
      );
      const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
        matchesId
      );

      const filtersContainer = document.querySelector('.js-filter');

      if (filtersContainer) {
        facetsToRender.forEach((elementToRender, index) => {
          const currentElement = document.getElementById(elementToRender.id);

          // Element already rendered in the DOM so just update the innerHTML
          if (currentElement) {
            currentElement.innerHTML = elementToRender.innerHTML;
          } else {
            if (index === 0) {
              // Insert the first element at the beginning of the container
              filtersContainer.prepend(elementToRender);
            } else {
              const previousElement = document.getElementById(
                facetsToRender[index - 1].id
              );

              if (previousElement) {
                previousElement.after(elementToRender);
              } else {
                // If the previous element doesn't exist, append it to the container
                filtersContainer.append(elementToRender);
              }
            }
          }
        });
      }

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (countsToRender) {
        const closestJSFilterID = event.target.closest('.js-filter').id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(
            countsToRender,
            event.target.closest('.js-filter')
          );
          FacetFiltersForm.renderMobileCounts(
            countsToRender,
            document.getElementById(closestJSFilterID)
          );

          const newFacetDetailsElement =
            document.getElementById(closestJSFilterID);
          const newElementSelector = newFacetDetailsElement.classList.contains(
            'mobile-facets__details'
          )
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
          const newElementToActivate =
            newFacetDetailsElement.querySelector(newElementSelector);

          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput)
            newElementToActivate.focus();
        }
      }
    } catch (error) {
      console.error('Error parsing filters HTML: ', error);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop'
    ];

    activeFacetElementSelectors.forEach(selector => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.sorting'];

    mobileElementSelectors.forEach(selector => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById('FacetFiltersFormMobile')
      .closest('menu-drawer')
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id
      }
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData).toString();
    return params;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form'
    );
    const forms = [];

    // Determine if the form is a mobile form
    const isMobileForm =
      event.target.closest('form').id === 'FacetSortFormMobile' ||
      event.target.closest('form').id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach(form => {
      if (!isMobileForm) {
        // For non-mobile forms, if the form ID matches specific IDs
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
          forms.push(this.createSearchParams(form)); // Add form parameters to the forms array
        }
      } else if (
        form.id === 'FacetSortFormMobile' ||
        form.id === 'FacetFiltersFormMobile'
      ) {
        // For mobile forms, if the form ID matches specific IDs
        forms.push(this.createSearchParams(form)); // Add form parameters to the forms array
      }
    });
    // Submit the form with the created search parameters joined as a query string
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', event => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
    // Update the href for all facet-remove components
    this.updateFacetRemoveHrefs();
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');

    // Trigger the removal of the filter
    form.onActiveFilterClick(event);
  }

  updateFacetRemoveHrefs() {
    const facets = document.querySelectorAll(
      'facet-remove:not([data-label="clear-all"])'
    );

    // Create a Set to track active filter parameters
    const activeFilters = new Set();

    if (facets.length > 0) {
      // Collect active filters from all facet-remove elements
      facets.forEach(facet => {
        const label = facet.getAttribute('data-label');
        const param = facet.getAttribute('data-param');

        if (label && param) {
          activeFilters.add(
            // encodeURI for spaces between param words and & sign
            `${encodeURIComponent(param)}=${encodeURIComponent(label)}`
          );
        }
      });
      // For each facet-remove link, update the href
      facets.forEach(facet => {
        const facetLink = facet.querySelector('a');
        const currentHref = new URL(
          facetLink.getAttribute('href'),
          window.location.origin
        );

        // Filter out parameters that are not in the active filters
        const updatedParams = [];

        currentHref.searchParams.forEach((value, key) => {
          // encodeURI for spaces between param words and & sign
          const param = `${encodeURIComponent(key)}=${encodeURIComponent(
            value
          )}`;
          // If the param is in activeFilters or is a sort/search param, keep it
          if (activeFilters.has(param) || key === 'sort_by' || key === 'q') {
            updatedParams.push(param);
          }
        });

        // Construct the new href
        const newHref = `${currentHref.pathname}?${updatedParams.join('&')}`;
        facetLink.setAttribute('href', newHref);
      });
    }
  }
}

customElements.define('facet-remove', FacetRemove);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('click', this.onBodyClick);
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach(button =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector('button')
      );
      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              'transitionend',
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden');
    document.querySelector('.mobile-facets__close')?.focus();
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach(submenu => {
        submenu.classList.remove('submenu-open');
      });
    removeTrapFocus(elementToFocus);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onBodyClick(event) {
    event.stopPropagation();
    if (event.target.classList.contains('filters-overlay')) {
      this.closest('.mobile-facets__wrapper')
        .querySelector('.filters-drawer')
        .removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
    document.body.classList.remove('overflow-hidden');
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
  }
}

customElements.define('menu-drawer', MenuDrawer);
